import * as React from 'react';
import type { FieldPath, UseFormGetValues, UseFormSetValue, UseFormUnregister } from 'react-hook-form';
import { type FieldValues, type UseControllerProps } from 'react-hook-form';

import { useEditingTableCellName } from 'af-root/hooks/useEditingTableCellName';

import type { Props as PlainDatePickerProps } from 'af-components/Controls/DateInput';

import EditModeCellWrapper from './EditModeCellWrapper';
import DatePicker from '../RHFFieldWrappers/DatePicker/DatePicker';
import DatePickerField from '../RHFFieldWrappers/DatePicker/DatePickerField';

type Props<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues,
> = {
	rhfId: number | string;
	onChange?: (name: string) => void;
	unregister: UseFormUnregister<TFieldValues>;
	getValues: UseFormGetValues<TFieldValues>;
	setValue: UseFormSetValue<TFieldValues>;
	fieldsLength: number;
	readonly?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDatePickerProps;

function DatePickerCellRenderer<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { control, name, onChange, unregister, setValue, readonly, placeholder } = props;

	const handleChange = React.useCallback(
		(exitEditMode: () => void) => () => {
			onChange?.(name);
			exitEditMode();
		}, [name, onChange]);

	const editingCellName = useEditingTableCellName();

	const returnComponent = React.useMemo(() => (
		<EditModeCellWrapper name={name} readonly={readonly}>
			{(shouldBeRegistered, exitEditMode) =>
				shouldBeRegistered ? (
					<DatePickerField
						{...props}
						control={control}
						key={name}
						onBlur={handleChange(exitEditMode)}
						onChange={handleChange(exitEditMode)}
						openFocused={editingCellName?.editingTableCellName === name ? true : false}
						placeholder={placeholder}
						setValue={setValue}
						unregister={unregister}
					/>
				) : (
					<DatePicker
						{...props}
						control={control}
						key={name}
						placeholder={placeholder}
					/>
				)
			}
		</EditModeCellWrapper>
	), [control, editingCellName?.editingTableCellName, handleChange, name, placeholder, props, readonly, setValue, unregister]);

	return returnComponent;
}

export default React.memo(DatePickerCellRenderer) as typeof DatePickerCellRenderer;
